import { Box, Heading, Text } from "@vinomofo/components";

import {
  WineClub,
  WineClubFrequency,
  WineClubPreference,
  WineClubVariant,
} from "../../contexts/wine-clubs/types";

const stringifyThings = (things: string[]): string => {
  if (things.length < 2) return things[0];

  const trunk = things.slice(0, -1);
  const [tail] = things.slice(-1);

  return trunk.join(", ") + " or " + tail;
};

interface ClubSummaryProps {
  club: WineClub;
  variant: WineClubVariant;
  frequency: WineClubFrequency;
  preference: WineClubPreference;
  color?: string;
  borderColor?: string;
  iconColor?: string;
  dynamicVariant?: boolean;
  dynamicFrequency?: boolean;
}

export const ClubSummary = ({
  club,
  variant,
  frequency,
  color = "white",
  borderColor = "dorianGray",
  iconColor = "white",
  dynamicVariant,
  dynamicFrequency,
}: ClubSummaryProps): JSX.Element => {
  if (!club) return null;

  const bottlePrice = Math.ceil(variant.price / variant.packSize);

  const variantSellingPoint = dynamicVariant
    ? variant.label
    : stringifyThings(club.variants.map((v) => v.label));

  const frequencySellingPoint = dynamicFrequency
    ? frequency.label
    : stringifyThings(club.frequencies.map((f) => f.label));

  return (
    <Box>
      <Heading
        as="div"
        fontWeight={600}
        variant="lg"
        textAlign="center"
        color={color}
      >
        <Box display="flex">
          <Box display="flex" mr="5px">
            <Text as="span" fontFamily="heading" color="inherit">
              $
            </Text>
            {variant.price}
          </Box>
          <Box display="flex" pt="2px">
            <Text
              as="span"
              textTransform="uppercase"
              fontWeight="400"
              fontSize="14px"
              color="inherit"
            >
              / {variant.packSize} PACK
            </Text>
          </Box>
        </Box>
      </Heading>

      <Box minHeight="46px" mb="10px">
        <Text variant="sm" color={color}>
          {club.description}
        </Text>
      </Box>

      <SellingPoint
        label={`$${bottlePrice} a bottle`}
        iconName="bottle"
        color={color}
        borderColor={borderColor}
        iconColor={iconColor}
      />

      {variant.freeShipping && (
        <SellingPoint
          label="Free shipping"
          iconName="truck"
          color={color}
          borderColor={borderColor}
          iconColor={iconColor}
        />
      )}

      <SellingPoint
        label={variant.rrp}
        iconName="dollar"
        color={color}
        borderColor={borderColor}
        iconColor={iconColor}
      />

      <SellingPoint
        label={
          variantSellingPoint === "Standard" ? "Mixed" : variantSellingPoint
        }
        iconName="box"
        color={color}
        borderColor={borderColor}
        iconColor={iconColor}
      />

      <SellingPoint
        label={frequencySellingPoint}
        iconName="calendar"
        color={color}
        borderColor={borderColor}
        iconColor={iconColor}
      />
    </Box>
  );
};

interface SellingPointProps {
  label: string;
  iconName: string;
  color: string;
  borderColor: string;
  iconColor: string;
}

const SellingPoint = ({
  label,
  iconName,
  color,
  borderColor,
  iconColor,
}: SellingPointProps) => {
  return (
    <Box
      borderTop="1px solid"
      borderTopColor={borderColor}
      display="flex"
      py="5px"
      alignItems="center"
      color={iconColor}
    >
      <Box as="svg" width="25px" height="25px" viewBox="0 0 20 20" mr="10px">
        {icons[iconName].map((d, i) => (
          <path fill="currentColor" key={i} d={d} />
        ))}
      </Box>
      <Text variant="sm" lineHeight="none" color={color}>
        {label}
      </Text>
    </Box>
  );
};

const icons = {
  bottle: [
    "M11.65 7.47H12l-.09 11h-.15zm-4.28.15l.1 11.71a.63.63 0 0 0 .63.63h3.71a.63.63 0 0 0 .63-.63l.15-11.71c0-1.35-1.72-1.88-1.72-2.72V1.12a.12.12 0 0 0 .18-.12V.56a.12.12 0 0 0-.12-.12V.21A.22.22 0 0 0 10.7 0H9.2a.15.15 0 0 0-.07.06v.38H9a.12.12 0 0 0-.12.12V1a.12.12 0 0 0 .12.12V4.9c0 .81-1.68 1.41-1.72 2.71z",
  ],
  truck: [
    "M7.76 13.64a.88.88 0 0 1 .87-.87.86.86 0 1 1-.87.87zm6-6.9a.19.19 0 0 1 .2-.19h1.46a.19.19 0 0 1 .16.09l1.61 2.53a.19.19 0 0 1-.16.29H14a.19.19 0 0 1-.2-.2zm.49 6.9a.88.88 0 0 1 .87-.87.87.87 0 0 1 .87.87.88.88 0 0 1-.87.87.87.87 0 0 1-.83-.87zm-12.42-8a.72.72 0 0 0-.71.77.73.73 0 0 0 .74.64h3.5a.72.72 0 1 1 0 1.42H3A.72.72 0 1 0 3 9.9h2.41a.72.72 0 1 1 0 1.43H4.13a.72.72 0 0 0-.69.78.73.73 0 0 0 .74.64h.58v.7a.77.77 0 0 0 .77.77h1.1a2.07 2.07 0 0 0 2 1.51 2.1 2.1 0 0 0 2-1.51h2.51a2.07 2.07 0 0 0 2 1.51 2.1 2.1 0 0 0 2-1.51h1a.77.77 0 0 0 .77-.77V10a1.37 1.37 0 0 0-.21-.73l-2.28-3.6a.84.84 0 0 0-.71-.39h-2.65V5a.78.78 0 0 0-.78-.78H5.52a.78.78 0 0 0-.78.78v.7H1.87z",
  ],
  dollar: [
    "M5.52 7.26c0-1.79 1.26-3.2 3.45-3.63V2.7a1 1 0 0 1 2 0v.79a8.7 8.7 0 0 1 1.87.27 1.19 1.19 0 0 1-.3 2.32 1.16 1.16 0 0 1-.33 0 6.25 6.25 0 0 0-1.81-.26c-1.32 0-1.74.57-1.74 1.15s.69 1.03 2.43 1.7c2.43.85 3.4 2 3.4 3.81s-1.29 3.36-3.64 3.77v1.08a1 1 0 1 1-2 0v-.93a9.42 9.42 0 0 1-2.43-.4 1.19 1.19 0 0 1-.78-1.42v-.1a1.15 1.15 0 0 1 1.13-.86 1.19 1.19 0 0 1 .39.07 6.87 6.87 0 0 0 2.23.31c1.16 0 2-.45 2-1.26S10.7 11.5 9.19 11C7 10.26 5.52 9.24 5.52 7.26z",
  ],
  box: [
    "M16 11.78l-3.6 1.66-.45.2-.33-.36-1.37-1.48v5.28L16 14.52zm-12.24 0v2.74l5.78 2.57v-5.28L8.2 13.29l-.33.36-.45-.2zM16.26 8l-6.1 2.71 2 2.12 5.95-2.72zM3.57 8l-1.82 2.1 5.95 2.72 2-2.12C7.64 9.79 5.59 8.91 3.57 8zm6.34-2.28L5 7.72l4.94 2 4.94-2zm2.21-3.09l-2 2.11 6.11 2.71 1.81-2.11zm-4.42 0L1.75 5.35l1.81 2.1 6.11-2.71z",
  ],
  calendar: [
    "M6.16 2.23a1 1 0 00-1 1v1.43h1.97V3.2a1 1 0 00-1-1zm9.7 13.59H4.22V7.57h11.64zm-1-11.16v1a1 1 0 01-1.94 0v-1H7.13v1a1 1 0 01-1.94 0v-1H2.28v13.1H17.8V4.66zm-.94-2.43a1 1 0 00-1 1v1.43h1.94V3.2a1 1 0 00-1-1z",
    "M9.07 14.75l-2.62-2.62 1.36-1.36 1.26 1.26 3.2-3.2 1.36 1.36-4.56 4.56z",
  ],
};
